import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  ImageOff,
  Tv,
  Sparkles,
  FolderOpen,
  Upload,
  Image,
  Video,
  CheckCircle2,
  ArrowRight,
  X,
} from 'lucide-react';
import type { RenderedVideo } from '@/types/video-enhancer';
import type { WizardStep, BackgroundSourceTab } from '@/hooks/useBackgroundReplacement';

interface ProjectFile {
  id: string;
  name: string;
  fileType?: 'video' | 'audio' | 'image' | 'srt';
  thumbnailUrl?: string;
  s3Url?: string;
  duration: number;
}

interface BackgroundReplacementWizardProps {
  open: boolean;
  step: WizardStep;
  video: RenderedVideo | null;
  maskUrl: string | null;
  projectFiles: ProjectFile[];
  onClose: () => void;
  onKeepGreenScreen: () => void;
  onSelectBackground: () => void;
  onComposite: (backgroundUrl: string, backgroundType: 'image' | 'video') => void;
  onGenerateAsset: (
    type: 'image' | 'video',
    prompt: string,
    options: { aspectRatio?: string }
  ) => void;
  generatingJob: { jobId: string; type: string; status: string; progress: number; assetUrl?: string } | null;
}

export const BackgroundReplacementWizard: React.FC<BackgroundReplacementWizardProps> = ({
  open,
  step,
  video,
  maskUrl,
  projectFiles,
  onClose,
  onKeepGreenScreen,
  onSelectBackground,
  onComposite,
  onGenerateAsset,
  generatingJob,
}) => {
  const [sourceTab, setSourceTab] = useState<BackgroundSourceTab>('generate');
  const [generateType, setGenerateType] = useState<'image' | 'video'>('image');
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [generateAspectRatio, setGenerateAspectRatio] = useState('16:9');
  const [selectedBackground, setSelectedBackground] = useState<string | null>(null);
  const [selectedBackgroundType, setSelectedBackgroundType] = useState<'image' | 'video'>('image');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [lastGeneratedUrl, setLastGeneratedUrl] = useState<string | null>(null);

  // Auto-select generated asset as background when generation completes
  useEffect(() => {
    if (generatingJob?.status === 'completed' && generatingJob.assetUrl) {
      setSelectedBackground(generatingJob.assetUrl);
      setSelectedBackgroundType(generatingJob.type === 'video' ? 'video' : 'image');
      setLastGeneratedUrl(generatingJob.assetUrl);
    }
  }, [generatingJob?.status, generatingJob?.assetUrl]);

  const imageAndVideoFiles = projectFiles.filter(
    f => f.fileType === 'image' || f.fileType === 'video'
  );

  const handleUpload = useCallback(async (file: File) => {
    setUploadingFile(true);
    try {
      const response = await fetch('/get-upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
        }),
      });

      const { uploadUrl, downloadUrl } = await response.json();

      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      const isVideo = file.type.startsWith('video/');
      setSelectedBackground(downloadUrl);
      setSelectedBackgroundType(isVideo ? 'video' : 'image');
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploadingFile(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.type.startsWith('image/') || file.type.startsWith('video/'))) {
      handleUpload(file);
    }
  }, [handleUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  }, [handleUpload]);

  const handleGenerate = useCallback(() => {
    if (!generatePrompt.trim()) return;
    onGenerateAsset(generateType, generatePrompt.trim(), {
      aspectRatio: generateAspectRatio,
    });
  }, [generateType, generatePrompt, generateAspectRatio, onGenerateAsset]);

  const handleComposite = useCallback(() => {
    if (selectedBackground) {
      onComposite(selectedBackground, selectedBackgroundType);
    }
  }, [selectedBackground, selectedBackgroundType, onComposite]);

  const stepIndex = step === 'removing' ? 0 : step === 'choose' ? 1 : 2;

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageOff className="h-5 w-5" />
            {step === 'removing'
              ? 'Removing Background...'
              : step === 'choose'
              ? 'Background Removed'
              : 'Select Background'}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 pb-2">
          {['Remove', 'Choose', 'Replace'].map((label, i) => (
            <React.Fragment key={label}>
              <div
                className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
                  i < stepIndex
                    ? 'bg-success/10 text-success'
                    : i === stepIndex
                    ? 'bg-primary/10 text-primary'
                    : 'bg-secondary text-muted-foreground/50'
                }`}
              >
                {i < stepIndex ? (
                  <CheckCircle2 className="h-3 w-3" />
                ) : i === stepIndex ? (
                  <div className="h-2 w-2 animate-pulse rounded-full bg-current" />
                ) : null}
                <span>{label}</span>
              </div>
              {i < 2 && (
                <ArrowRight className={`h-3 w-3 ${i < stepIndex ? 'text-success/50' : 'text-muted-foreground/30'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Removing */}
        {step === 'removing' && (
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="relative">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <ImageOff className="absolute inset-0 m-auto h-5 w-5 text-primary" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">Processing {video?.name}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                AI is removing the background from your video...
              </p>
            </div>
            {video?.bgRemovalProgress !== undefined && video.bgRemovalProgress > 0 && (
              <div className="w-full max-w-xs">
                <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                  <div
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${video.bgRemovalProgress}%` }}
                  />
                </div>
                <p className="mt-1 text-center text-[10px] text-muted-foreground">
                  {video.bgRemovalProgress}%
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Choose action */}
        {step === 'choose' && (
          <div className="space-y-3 py-2">
            <p className="text-center text-xs text-muted-foreground">
              Background has been removed. What would you like to do?
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Card
                className="cursor-pointer border-2 border-transparent p-4 text-center transition-colors hover:border-success/50 hover:bg-success/5"
                onClick={onKeepGreenScreen}
              >
                <Tv className="mx-auto mb-2 h-8 w-8 text-success" />
                <h4 className="text-sm font-medium">Keep Green Screen</h4>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Save the background-removed version as-is
                </p>
              </Card>
              <Card
                className="cursor-pointer border-2 border-transparent p-4 text-center transition-colors hover:border-primary/50 hover:bg-primary/5"
                onClick={onSelectBackground}
              >
                <Sparkles className="mx-auto mb-2 h-8 w-8 text-primary" />
                <h4 className="text-sm font-medium">Replace Background</h4>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Choose a new background image or video
                </p>
              </Card>
            </div>
          </div>
        )}

        {/* Step 3: Select background */}
        {step === 'select-background' && (
          <div className="space-y-4">
            <Tabs
              value={sourceTab}
              onValueChange={(v) => setSourceTab(v as BackgroundSourceTab)}
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="generate" className="flex items-center gap-1.5 text-xs">
                  <Sparkles className="h-3.5 w-3.5" />
                  Generate
                </TabsTrigger>
                <TabsTrigger value="project" className="flex items-center gap-1.5 text-xs">
                  <FolderOpen className="h-3.5 w-3.5" />
                  From Project
                </TabsTrigger>
                <TabsTrigger value="upload" className="flex items-center gap-1.5 text-xs">
                  <Upload className="h-3.5 w-3.5" />
                  Upload
                </TabsTrigger>
              </TabsList>

              {/* Generate tab */}
              <TabsContent value="generate" className="mt-3 space-y-3">
                <div className="flex gap-2">
                  <Button
                    variant={generateType === 'image' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 gap-1.5 text-xs"
                    onClick={() => setGenerateType('image')}
                  >
                    <Image className="h-3 w-3" />
                    Image
                  </Button>
                  <Button
                    variant={generateType === 'video' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 gap-1.5 text-xs"
                    onClick={() => setGenerateType('video')}
                  >
                    <Video className="h-3 w-3" />
                    Video
                  </Button>
                </div>
                <Textarea
                  placeholder={`Describe the ${generateType} background you want...`}
                  value={generatePrompt}
                  onChange={(e) => setGeneratePrompt(e.target.value)}
                  className="min-h-[60px] resize-none text-xs"
                />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Aspect:</span>
                  <Select value={generateAspectRatio} onValueChange={setGenerateAspectRatio}>
                    <SelectTrigger className="h-7 w-24 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="16:9">16:9</SelectItem>
                      <SelectItem value="9:16">9:16</SelectItem>
                      <SelectItem value="1:1">1:1</SelectItem>
                      <SelectItem value="4:3">4:3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  size="sm"
                  className="w-full gap-2"
                  onClick={handleGenerate}
                  disabled={!generatePrompt.trim() || (generatingJob?.status === 'processing')}
                >
                  {generatingJob?.status === 'processing' ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5" />
                      Generate {generateType === 'image' ? 'Image' : 'Video'}
                    </>
                  )}
                </Button>
                {generatingJob?.status === 'completed' && lastGeneratedUrl && (
                  <div className="rounded-lg border border-success/30 bg-success/5 p-2 text-center">
                    <p className="text-xs text-success">Generated! Click "Composite Video" below.</p>
                  </div>
                )}
              </TabsContent>

              {/* From Project tab */}
              <TabsContent value="project" className="mt-3">
                {imageAndVideoFiles.length === 0 ? (
                  <div className="py-8 text-center">
                    <FolderOpen className="mx-auto mb-2 h-8 w-8 text-muted-foreground/30" />
                    <p className="text-xs text-muted-foreground">
                      No image or video files in your project
                    </p>
                  </div>
                ) : (
                  <div className="grid max-h-[200px] grid-cols-3 gap-2 overflow-y-auto">
                    {imageAndVideoFiles.map((file) => (
                      <div
                        key={file.id}
                        className={`group cursor-pointer overflow-hidden rounded-lg border-2 transition-colors ${
                          selectedBackground === file.s3Url
                            ? 'border-primary bg-primary/5'
                            : 'border-transparent hover:border-primary/30'
                        }`}
                        onClick={() => {
                          setSelectedBackground(file.s3Url || null);
                          setSelectedBackgroundType(file.fileType === 'video' ? 'video' : 'image');
                        }}
                      >
                        <div className="aspect-video bg-[hsl(var(--timeline))]">
                          {file.thumbnailUrl || file.fileType === 'image' ? (
                            <img
                              src={file.thumbnailUrl || file.s3Url}
                              alt={file.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <Video className="h-4 w-4 text-muted-foreground/50" />
                            </div>
                          )}
                        </div>
                        <p className="truncate px-1 py-0.5 text-[9px] text-muted-foreground">
                          {file.name}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Upload tab */}
              <TabsContent value="upload" className="mt-3">
                <div
                  className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                    uploadingFile
                      ? 'border-primary/50 bg-primary/5'
                      : 'border-muted-foreground/25 hover:border-primary/50'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => document.getElementById('bg-upload-input')?.click()}
                >
                  {uploadingFile ? (
                    <>
                      <Loader2 className="mx-auto mb-2 h-8 w-8 animate-spin text-primary" />
                      <p className="text-xs text-muted-foreground">Uploading...</p>
                    </>
                  ) : selectedBackground && sourceTab === 'upload' ? (
                    <>
                      <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-success" />
                      <p className="text-xs text-success">File uploaded</p>
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        Click to change or drag a new file
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
                      <p className="text-xs text-muted-foreground">
                        Drop an image or video here, or click to browse
                      </p>
                    </>
                  )}
                  <input
                    id="bg-upload-input"
                    type="file"
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>
              </TabsContent>
            </Tabs>

            {/* Selected background preview & composite button */}
            {selectedBackground && (
              <div className="space-y-2 rounded-lg border border-border bg-secondary/20 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">Selected Background</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => setSelectedBackground(null)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                {selectedBackgroundType === 'image' && (
                  <img
                    src={selectedBackground}
                    alt="Background preview"
                    className="h-20 w-full rounded object-cover"
                  />
                )}
                {selectedBackgroundType === 'video' && (
                  <div className="flex h-20 w-full items-center justify-center rounded bg-[hsl(var(--timeline))]">
                    <Video className="h-6 w-6 text-muted-foreground/50" />
                    <span className="ml-2 text-xs text-muted-foreground">Video background</span>
                  </div>
                )}
              </div>
            )}

            {/* Composite button */}
            {video?.bgCompositeStatus === 'processing' ? (
              <Button size="sm" className="w-full gap-2" disabled>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Compositing Video...
              </Button>
            ) : (
              <Button
                size="sm"
                className="w-full gap-2"
                onClick={handleComposite}
                disabled={!selectedBackground}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Composite Video
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
